import { css, unsafeCSS } from 'lit';
import { type DeepChat } from 'deep-chat';
import { theme } from '@atlas/shared/client/ui/core/theming';
import { FontFamilyLrFontFamilyDefault, SizeLrBreakpointMd, SizeLrBreakpointLg } from '@laredoute/design-system/tokens';

export const chatbotStyle = css`
    :host {
        --chat-sticky-offset-right: 100px;
        --chat-sticky-offset-bottom: 12%;
        --chat-sticky-width: 100%;
        --chat-sticky-min-width: 360px;
        --chat-sticky-minimized-size: 80px;
        --chat-sticky-offset-left: calc(100% - var(--chat-sticky-minimized-size));
        --chat-menu-height: 50px;
        --font-family: ${unsafeCSS(FontFamilyLrFontFamilyDefault)};
        font-family: var(--font-family);
    }

    :host #chat-wrapper {
        position: relative;
    }

    :host #chat-element {
        height: 100% !important;
        max-width: var(--chat-sticky-width) !important;

        @media (min-width: ${unsafeCSS(SizeLrBreakpointMd)}) {
            width: 45vw !important;
        }

        @media (min-width: ${unsafeCSS(SizeLrBreakpointLg)}) {
            width: 25vw !important;
        }
    }

    #chat-wrapper {
        display: block;
        font-family: var(--font-poppins);
        transform-origin: bottom;
    }

    #chat-wrapper[data-sticky='true'] {
        position: fixed;
        bottom: 0;
        margin-left: -8px;
        z-index: 10001;
        box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.16), 0px 4px 8px 2px rgba(0, 0, 0, 0.08);

        @media (min-width: ${unsafeCSS(SizeLrBreakpointMd)}) {
            right: var(--chat-sticky-offset-right);
            margin-left: inherit;
            top: inherit;
            --chat-sticky-width: max(45vw, --chat-sticky-min-width);
            --chat-sticky-offset-right: 100px;
        }

        @media (min-width: ${unsafeCSS(SizeLrBreakpointLg)}) {
            --chat-sticky-width: max(25vw, --chat-sticky-min-width);
        }
    }

    #chat-wrapper[data-opened='true'] {
        @media (max-width: ${unsafeCSS(SizeLrBreakpointMd)}) {
            top: 0;
        }
    }

    #chat-wrapper[data-opened='false'] {
        bottom: -100%;
        opacity: 0;
    }

    #chat-wrapper-menu {
        background-color: #cbd2ff;
        color: white;
        height: var(--chat-menu-height);
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: ${unsafeCSS(theme.colors.base.greys.tone900)};

        @media (min-width: ${unsafeCSS(SizeLrBreakpointMd)}) {
            border-top-left-radius: 4px;
            border-top-right-radius: 4px;
        }
    }

    #chat-wrapper-menu-button-container {
        display: flex;
        align-items: center;
        margin: 0 1rem;

        lr-icon-button::part(base) {
            color: currentColor;
        }
    }

    #chat-wrapper-menu-title {
        font-size: 1.25rem;
        font-weight: 600;
        margin-left: 1rem;
    }

    :host #chat-minimized {
        position: fixed;
        cursor: pointer;
        width: var(--chat-sticky-minimized-size);
        height: var(--chat-sticky-minimized-size);
        bottom: var(--chat-sticky-offset-bottom);
        z-index: 10001;
        left: var(--chat-sticky-offset-left);
        background-color: #cbd2ff;
        border-radius: 16px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.16), 0px 4px 8px 2px rgba(0, 0, 0, 0.08);
        transform: scale(1) translate(-50%, 0%);
        transition: transform 0.2s cubic-bezier(0.64, 0.57, 0.67, 1.53);
        color: ${unsafeCSS(theme.colors.base.greys.tone900)};
        font-family: var(--font-poppins);
        &:hover {
            transform: scale(1.1) translate(-50%, 0%);
        }

        @media (min-width: ${unsafeCSS(SizeLrBreakpointMd)}) {
            --chat-sticky-width: 45vw;
            --chat-sticky-offset-bottom: 5%;
            --chat-sticky-offset-left: calc(
                100% -
                    (
                        var(--chat-sticky-offset-right) +
                            ((var(--chat-sticky-width) / 2) - var(--chat-sticky-minimized-size))
                    )
            );
            transform: scale(1) translate(0%, 0%);
            &:hover {
                transform: scale(1.1) translate(0%, 0%);
            }
        }

        @media (min-width: ${unsafeCSS(SizeLrBreakpointLg)}) {
            --chat-sticky-width: 25vw;
        }

        &.isDraggable {
            cursor: grab;
            user-select: none;
        }
    }

    p {
        margin: 0;
        font-size: 0.9rem;
        font-weight: 600;
    }

    #chat-wrapper-content {
        position: relative;
        overflow: hidden;
        background-color: ${unsafeCSS(theme.colors.base.white)};
    }

    #exit-overlay {
        width: 100%;
        position: absolute;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 0 2rem;
        background-color: #f9f9fb;
        z-index: 1001;
        height: 100%;

        p {
            font-size: 0.75rem;
            font-weight: 400;
            text-align: center;
        }

        div {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            width: fit-content;
            margin: 2rem auto;
            lr-button {
                width: 100%;
            }
        }
    }
`;

export const DeepChatStyle: Partial<DeepChat> = {
    avatars: {
        ai: {
            src: new URL('../../assets/avatars/lr.svg', import.meta.url).href
        },
        user: {
            src: new URL('../../assets/avatars/user.svg', import.meta.url).href
        },
        lr: {
            src: new URL('../../assets/avatars/lr.svg', import.meta.url).href,
            styles: {
                avatar: {
                    display: 'none'
                },
                container: {
                    display: 'none',
                    marginBottom: '0'
                }
            }
        },
        default: {
            styles: {
                container: {
                    backgroundColor: theme.colors.base.white,
                    borderRadius: '50%',
                    padding: theme.spacing(1),
                    width: '20px',
                    height: '20px',
                    marginTop: '0'
                },
                avatar: {
                    padding: '0',
                    width: '20px',
                    height: '20px'
                }
            }
        }
    },
    chatStyle: {
        fontFamily: FontFamilyLrFontFamilyDefault,
        border: 'none',
        backgroundColor: theme.colors.base.greys.tone100,
        overflow: 'unset',
        width: '100%'
    },
    messageStyles: {
        default: {
            shared: {
                bubble: {
                    backgroundColor: theme.colors.base.white,
                    color: theme.colors.base.greys.tone900,
                    fontSize: '0.75rem',
                    padding: '0.625rem',
                    fontFamily: FontFamilyLrFontFamilyDefault,
                    lineHeight: '18px',
                    maxWidth: 'calc(100% - 4rem)',
                    width: '100%',
                    marginTop: '0'
                },
                innerContainer: {
                    alignItems: 'center'
                },
                outerContainer: {
                    margin: '0.5rem 0'
                }
            },
            lr: {
                bubble: {
                    background: theme.colors.base.greys.tone200,
                    color: theme.colors.base.black,
                    padding: '0.625rem',
                    fontSize: '0.75rem',
                    borderBottomLeftRadius: '0.5rem',
                    borderBottomRightRadius: '0.5rem',
                    borderTopRightRadius: '0',
                    borderTopLeftRadius: '0',
                    border: '2px solid white',
                    width: '100%',
                    maxWidth: '100%'
                },
                outerContainer: {
                    margin: '0'
                },
                innerContainer: {
                    marginBottom: '1rem',
                    width: '100%'
                }
            }
        },
        loading: {
            bubble: {
                padding: '.6em .75em .6em 1.3em',
                width: '1em'
            }
        }
    },
    textInput: {
        characterLimit: 256,
        styles: {
            container: {
                border: '0',
                borderRadius: '0',
                margin: '0',
                display: 'flex',
                boxShadow: 'none',
                width: '90%'
            }
        }
    },
    submitButtonStyles: {
        submit: {
            svg: {
                content: `<svg width="18" height="18" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.95622 0.777695L22.2495 11.1088C22.9779 11.4797 22.9779 12.5203 22.2495 12.8911L1.95622 23.2223C1.19182 23.6114 0.32436 22.9207 0.532399 22.0886L2.61818 13.7454L13.0909 12L2.61818 10.2545L0.532399 1.91139C0.32436 1.07924 1.19182 0.388542 1.95622 0.777695Z" fill="#CDD2DA"/>
                </svg>
                `,
                styles: {
                    default: {
                        width: '20px',
                        margin: '0',
                        padding: '0',
                        position: 'relative',
                        filter: 'brightness(0) saturate(100%) invert(40%) sepia(0%) saturate(5564%) hue-rotate(207deg) brightness(100%) contrast(97%)'
                    }
                }
            },
            container: {
                default: {
                    position: 'revert',
                    width: 'auto',
                    height: 'auto',
                    cursor: 'default',
                    margin: '0',
                    display: 'flex'
                },
                hover: {
                    background: 'none',
                    cursor: 'pointer'
                }
            }
        },

        position: 'outside-right',
        loading: {
            svg: {
                content: `<svg width="18" height="18" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.95622 0.777695L22.2495 11.1088C22.9779 11.4797 22.9779 12.5203 22.2495 12.8911L1.95622 23.2223C1.19182 23.6114 0.32436 22.9207 0.532399 22.0886L2.61818 13.7454L13.0909 12L2.61818 10.2545L0.532399 1.91139C0.32436 1.07924 1.19182 0.388542 1.95622 0.777695Z" fill="#CDD2DA"/>
                </svg>
                `,
                styles: {
                    default: {
                        filter: 'none'
                    }
                }
            }
        },
        disabled: {
            svg: {
                content: `<svg width="18" height="18" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.95622 0.777695L22.2495 11.1088C22.9779 11.4797 22.9779 12.5203 22.2495 12.8911L1.95622 23.2223C1.19182 23.6114 0.32436 22.9207 0.532399 22.0886L2.61818 13.7454L13.0909 12L2.61818 10.2545L0.532399 1.91139C0.32436 1.07924 1.19182 0.388542 1.95622 0.777695Z" fill="#CDD2DA"/>
                </svg>
                `,
                styles: {
                    default: {
                        filter: 'brightness(0) saturate(100%) invert(95%) sepia(0%) saturate(5564%) hue-rotate(207deg) brightness(100%) contrast(97%)'
                    }
                }
            }
        }
    },
    inputAreaStyle: {
        background: 'white',
        padding: '0.5rem 0.75rem',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '0.75rem',
        width: 'calc(100% - 1.5rem)',
        margin: '0'
    },
    htmlClassUtilities: {
        'button-container': {
            styles: {
                default: {
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem',
                    marginTop: '0.5rem'
                }
            }
        }
    },
    auxiliaryStyle: `
        #messages {
            height: calc(100dvh - 6rem);
            display: flex;
            flex-direction: column;
            @media (min-width: 768px)
            {
            height: 60dvh;
            }
        } 
        .outer-message-container:last-child
        {
            margin-bottom: 1rem;
        } 
        .outer-message-container:nth-child(2)
        {
            margin-block-start: auto !important;
        }
        .text-input-container-right-adjustment{
        margin: 0 !important;
        }   
        `
};
